import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { API } from './dosHelpers';

export function useDOSDomium(getAuthHeader, userRole) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [souscriptions, setSouscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // V37.91 — Tri par colonne
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'date' || col === 'montant' ? 'desc' : 'asc');
    }
    setPage(1);
  };
  const sortIcon = (col) => (sortBy !== col
    ? <ArrowUpDown className="h-3 w-3 opacity-40" />
    : (sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-violet-600" /> : <ArrowDown className="h-3 w-3 text-violet-600" />));
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [attestations, setAttestations] = useState([]);
  const [attPage, setAttPage] = useState(1);
  const [attTotalPages, setAttTotalPages] = useState(1);
  const [attTotal, setAttTotal] = useState(0);
  const [attTypeFilter, setAttTypeFilter] = useState('all');
  const [exportLoading, setExportLoading] = useState(null);
  const [refreshingCache, setRefreshingCache] = useState(null); // reference en cours
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [lastRefreshJob, setLastRefreshJob] = useState(null);
  const [refreshHistory, setRefreshHistory] = useState([]);
  const [showRefreshHistory, setShowRefreshHistory] = useState(false);

  // Aperçu PDF en iframe modale
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: null, reference: null, type: null, loading: false });

  // Dialog d'envoi du Pack complet par email au MO
  // V37.93 — cc_emails : destinataires supplémentaires (Super Admin)
  const [sendEmail, setSendEmail] = useState({ open: false, reference: null, to_email: '', to_name: '', message: '', cc_emails: [], sending: false });

  // Modale Rectification document signe (Super Admin)
  const [rectifyModal, setRectifyModal] = useState({ open: false, reference: null });

  // V37.92 — Dialog d'envoi du lien de signature électronique de
  // l'attestation nominative au Maître d'ouvrage (OTP email)
  const [nomSign, setNomSign] = useState({ open: false, reference: null, to_email: '', message: '', sending: false });

  // Libération de l'URL blob quand la modale se ferme ou change
  useEffect(() => {
    return () => {
      if (pdfPreview.url) {
        window.URL.revokeObjectURL(pdfPreview.url);
      }
    };
  }, [pdfPreview.url]);

  const isAdmin = userRole === 'super_admin' || userRole === 'admin';
  const isSuperAdmin = userRole === 'super_admin';

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dos-domium-dashboard/stats`, { headers: getAuthHeader() });
      setStats(res.data);
    } catch {
    }
  }, [getAuthHeader]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: pageSize.toString(), statut: statusFilter, sort_by: sortBy, sort_dir: sortDir });
      if (searchQuery) params.append('search', searchQuery);
      const res = await axios.get(`${API}/dos-domium-dashboard/list?${params}`, { headers: getAuthHeader() });
      setSouscriptions(res.data.souscriptions || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, searchQuery, sortBy, sortDir, getAuthHeader]);

  const fetchAttestations = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: attPage.toString(), limit: '20', type_att: attTypeFilter });
      const res = await axios.get(`${API}/dos-domium-dashboard/attestations?${params}`, { headers: getAuthHeader() });
      setAttestations(res.data.attestations || []);
      setAttTotalPages(res.data.pages || 1);
      setAttTotal(res.data.total || 0);
    } catch {
    }
  }, [attPage, attTypeFilter, getAuthHeader]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { if (activeTab === 'attestations') fetchAttestations(); }, [activeTab, fetchAttestations]);

  const handleExportCSV = async () => {
    setExportLoading('csv');
    try {
      const res = await axios.post(`${API}/dos-domium-dashboard/export/csv`, { statut: statusFilter, search: searchQuery, sort_by: sortBy, sort_dir: sortDir }, {
        headers: getAuthHeader(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `dos_domium_${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Export CSV telecharge');
    } catch { toast.error('Erreur export CSV'); }
    finally { setExportLoading(null); }
  };

  const handleExportExcel = async () => {
    setExportLoading('excel');
    try {
      const res = await axios.post(`${API}/dos-domium-dashboard/export/excel`, { statut: statusFilter, search: searchQuery, sort_by: sortBy, sort_dir: sortDir }, {
        headers: getAuthHeader(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `dos_domium_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Export Excel telecharge');
    } catch { toast.error('Erreur export Excel'); }
    finally { setExportLoading(null); }
  };

  const refreshAll = () => { fetchStats(); fetchList(); if (activeTab === 'attestations') fetchAttestations(); };

  const handleViewDomiumDoc = async (reference, type) => {
    // Révoque l'ancien blob URL s'il existe
    if (pdfPreview.url) {
      window.URL.revokeObjectURL(pdfPreview.url);
    }
    setPdfPreview({ open: true, url: null, reference, type, loading: true });
    try {
      // type: 'draft' | 'signed' → attestation ; 'cpcg' → dossier-maitre (CP + CG + Note)
      //       'pack' → pack-complet (Couverture + Sommaire + Attestation + CP + CG + Note)
      //       'nominative' → attestation nominative officielle (V37.83, QR vérification)
      let endpoint;
      if (type === 'cpcg') endpoint = 'dossier-maitre';
      else if (type === 'pack') endpoint = 'pack-complet';
      else if (type === 'nominative') endpoint = 'attestation-nominative';
      else endpoint = type === 'draft' ? 'attestation-draft' : 'attestation';
      const res = await axios.get(`${API}/domium/documents/${reference}/${endpoint}`, {
        headers: getAuthHeader(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfPreview({ open: true, url, reference, type, loading: false });
    } catch {
      setPdfPreview({ open: false, url: null, reference: null, type: null, loading: false });
      const msg = type === 'pack' ? 'Erreur visualisation Pack complet'
                : type === 'cpcg' ? 'Erreur visualisation CP/CG'
                : type === 'nominative' ? "Attestation nominative indisponible : l'attestation et les CP/CG doivent être signés"
                : 'Erreur visualisation attestation';
      toast.error(msg);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview.url) {
      window.URL.revokeObjectURL(pdfPreview.url);
    }
    setPdfPreview({ open: false, url: null, reference: null, type: null, loading: false });
  };

  const downloadFromPreview = () => {
    if (!pdfPreview.url || !pdfPreview.reference) return;
    const a = document.createElement('a');
    a.href = pdfPreview.url;
    if (pdfPreview.type === 'pack') {
      a.download = `Pack_Complet_DOMIUM_${pdfPreview.reference}.pdf`;
    } else if (pdfPreview.type === 'cpcg') {
      a.download = `Dossier_Maitre_DOMIUM_${pdfPreview.reference}.pdf`;
    } else if (pdfPreview.type === 'nominative') {
      a.download = `Attestation_Nominative_DOMIUM_${pdfPreview.reference}.pdf`;
    } else {
      a.download = `Attestation_${pdfPreview.type === 'draft' ? 'DRAFT' : 'Signee'}_${pdfPreview.reference}.pdf`;
    }
    a.click();
    const msg = pdfPreview.type === 'pack' ? 'Pack complet téléchargé'
              : pdfPreview.type === 'cpcg' ? 'Dossier Maître (CP/CG) téléchargé'
              : pdfPreview.type === 'nominative' ? 'Attestation nominative téléchargée'
              : 'Attestation telechargee';
    toast.success(msg);
  };

  const fetchLastRefreshAllJob = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/domium/admin/refresh-cache-all/last-job`, {
        headers: getAuthHeader(),
      });
      setLastRefreshJob(res.data?.job || null);
    } catch {
      // silencieux : pas critique
    }
  }, [getAuthHeader]);

  const fetchRefreshHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/domium/admin/refresh-cache-all/history?limit=10`, {
        headers: getAuthHeader(),
      });
      setRefreshHistory(res.data?.jobs || []);
    } catch {
      // silencieux
    }
  }, [getAuthHeader]);

  // Polling automatique tant qu'on est en mode "régénération globale"
  useEffect(() => {
    if (!refreshingAll) return;
    const id = setInterval(() => fetchLastRefreshAllJob(), 8000);
    return () => clearInterval(id);
  }, [refreshingAll, fetchLastRefreshAllJob]);

  // Fetch initial du dernier job pour afficher la date du dernier refresh global
  useEffect(() => {
    if (isAdmin) fetchLastRefreshAllJob();
  }, [isAdmin, fetchLastRefreshAllJob]);

  const handleRefreshCacheAll = async () => {
    if (!window.confirm(
      `Régénérer TOUS les PDFs de TOUTES les souscriptions DOMIUM ?\n\n` +
      `• Purge complète du cache MongoDB pdf_cache\n` +
      `• Suppression des copies R2 Pack complet (les liens email actifs deviennent invalides)\n` +
      `• Régénération in-place des PDFs en GED (CP, CG, Dossier Maître, Attestations)\n\n` +
      `Cette opération peut prendre plusieurs minutes selon le nombre de souscriptions.\n` +
      `Elle s'exécute en arrière-plan, vous pouvez continuer à utiliser l'application.`
    )) return;
    setRefreshingAll(true);
    try {
      const res = await axios.post(`${API}/domium/admin/refresh-cache-all`, {}, {
        headers: getAuthHeader(),
      });
      toast.success(res.data?.message || 'Régénération globale lancée');
      // Premier fetch du dernier job (peut être encore l'ancien tant que le nouveau n'est pas terminé)
      fetchLastRefreshAllJob();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors du lancement de la régénération';
      toast.error(detail);
      setRefreshingAll(false);
    }
  };

  // Détecte la fin de la tâche de régénération en arrière-plan (le job apparaît avec finished_at)
  useEffect(() => {
    if (refreshingAll && lastRefreshJob?.finished_at) {
      const finishedRecently = (
        new Date() - new Date(lastRefreshJob.finished_at)
      ) < 5 * 60 * 1000; // moins de 5 min
      if (finishedRecently) {
        toast.success(
          `Régénération globale terminée : ${lastRefreshJob.regenerated}/${lastRefreshJob.regenerated_total} PDFs régénérés` +
          (lastRefreshJob.regen_errors ? ` (${lastRefreshJob.regen_errors} erreur(s))` : '')
        );
        setRefreshingAll(false);
        fetchList();
      }
    }
  }, [lastRefreshJob, refreshingAll, fetchList]);

  const handleRefreshCache = async (reference) => {
    if (!reference) return;
    if (!window.confirm(
      `Régénérer tous les PDFs (Attestation, CP, CG, Dossier Maître, Pack complet) pour la souscription ${reference} ?\n\n` +
      `• Le cache MongoDB est purgé\n` +
      `• La copie R2 du Pack complet est supprimée (le prochain envoi par email régénérera un lien)\n` +
      `• Les PDFs en GED sont re-générés avec les templates à jour\n\n` +
      `Action recommandée pour propager les correctifs récents (logo, placeholders, dates, etc.).`
    )) return;
    setRefreshingCache(reference);
    try {
      const res = await axios.post(`${API}/domium/documents/${reference}/refresh-cache`, {}, {
        headers: getAuthHeader(),
      });
      toast.success(res.data?.message || 'Cache rafraîchi avec succès');
      // Refresh la liste pour mettre à jour les statuts d'attestation éventuels
      fetchList();
      if (activeTab === 'attestations') fetchAttestations();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors du rafraîchissement du cache';
      toast.error(detail);
    } finally {
      setRefreshingCache(null);
    }
  };

  const handleDownloadDomiumDoc = async (reference, type) => {
    try {
      let endpoint;
      if (type === 'cpcg') endpoint = 'dossier-maitre';
      else if (type === 'pack') endpoint = 'pack-complet';
      else if (type === 'nominative') endpoint = 'attestation-nominative';
      else endpoint = type === 'draft' ? 'attestation-draft' : 'attestation';
      const res = await axios.get(`${API}/domium/documents/${reference}/${endpoint}`, {
        headers: getAuthHeader(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'pack' ? `Pack_Complet_DOMIUM_${reference}.pdf`
                : type === 'cpcg' ? `Dossier_Maitre_DOMIUM_${reference}.pdf`
                : type === 'nominative' ? `Attestation_Nominative_DOMIUM_${reference}.pdf`
                : `Attestation_${type === 'draft' ? 'DRAFT' : 'Signee'}_${reference}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      const msg = type === 'pack' ? 'Pack complet téléchargé'
                : type === 'cpcg' ? 'Dossier Maître (CP/CG) téléchargé'
                : type === 'nominative' ? 'Attestation nominative téléchargée'
                : 'Attestation telechargee';
      toast.success(msg);
    } catch {
      const errMsg = type === 'pack' ? 'Erreur telechargement Pack complet'
                   : type === 'cpcg' ? 'Erreur telechargement CP/CG'
                   : type === 'nominative' ? "Attestation nominative indisponible : l'attestation et les CP/CG doivent être signés"
                   : 'Erreur telechargement attestation';
      toast.error(errMsg);
    }
  };

  const openSendEmailDialog = (reference, adherentEmail = '', adherentName = '') => {
    setSendEmail({ open: true, reference, to_email: adherentEmail || '', to_name: adherentName || '', message: '', cc_emails: [], sending: false });
  };

  const submitSendEmail = async () => {
    if (!sendEmail.to_email || !sendEmail.reference) return;
    setSendEmail(prev => ({ ...prev, sending: true }));
    try {
      // V37.93 — destinataires supplémentaires (Super Admin)
      const extras = (sendEmail.cc_emails || []).map(e => e.trim()).filter(Boolean);
      const res = await axios.post(
        `${API}/domium/documents/${sendEmail.reference}/send-pack-email`,
        { to_email: sendEmail.to_email, to_name: sendEmail.to_name, message: sendEmail.message, additional_emails: extras },
        { headers: getAuthHeader() }
      );
      const sentList = res?.data?.sent || [sendEmail.to_email];
      const failedList = res?.data?.failed || [];
      toast.success(sentList.length > 1
        ? `Pack complet envoyé à ${sentList.length} destinataires : ${sentList.join(', ')}`
        : `Pack complet envoyé à ${sentList[0]}`);
      if (failedList.length) toast.error(`Échec d'envoi pour : ${failedList.join(', ')}`);
      const expDate = res?.data?.expires_at ? new Date(res.data.expires_at).toLocaleDateString('fr-FR') : '';
      if (expDate) toast.info(`Lien sécurisé valide jusqu'au ${expDate}`);
      setSendEmail({ open: false, reference: null, to_email: '', to_name: '', message: '', cc_emails: [], sending: false });
      // Rafraîchir la liste pour afficher le badge « Pack envoyé »
      fetchList();
      fetchAttestations();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de l'envoi de l'email";
      toast.error(msg);
      setSendEmail(prev => ({ ...prev, sending: false }));
    }
  };

  const [gedLoading, setGedLoading] = useState(null);

  // V37.92 — Envoi du lien de signature électronique de l'attestation
  // nominative au Maître d'ouvrage (OTP email, validité 7 jours)
  const openNomSignDialog = (reference, moEmail = '') => {
    setNomSign({ open: true, reference, to_email: moEmail || '', message: '', sending: false });
  };

  const submitNomSign = async () => {
    if (!nomSign.to_email || !nomSign.reference) return;
    setNomSign(prev => ({ ...prev, sending: true }));
    try {
      const res = await axios.post(
        `${API}/domium/admin/documents/${nomSign.reference}/send-nominative-signature`,
        { to_email: nomSign.to_email, message: nomSign.message },
        { headers: getAuthHeader() }
      );
      toast.success(`Lien de signature envoyé à ${res?.data?.to_email || nomSign.to_email}`);
      const expDate = res?.data?.expires_at ? new Date(res.data.expires_at).toLocaleDateString('fr-FR') : '';
      if (expDate) toast.info(`Lien sécurisé valide jusqu'au ${expDate}`);
      setNomSign({ open: false, reference: null, to_email: '', message: '', sending: false });
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi du lien de signature");
      setNomSign(prev => ({ ...prev, sending: false }));
    }
  };

  const handleClasserGED = async (reference, type) => {
    const key = `${reference}-${type}`;
    setGedLoading(key);
    try {
      const res = await axios.post(`${API}/dos-domium-dashboard/classer-ged/${reference}`, { type }, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Classe dans la GED');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur classement GED');
    } finally { setGedLoading(null); }
  };

  return { activeTab, setActiveTab, stats, setStats, souscriptions, setSouscriptions, loading, setLoading, page, setPage, pageSize, setPageSize, sortBy, setSortBy, sortDir, setSortDir, toggleSort, sortIcon, totalPages, setTotalPages, total, setTotal, searchQuery, setSearchQuery, statusFilter, setStatusFilter, attestations, setAttestations, attPage, setAttPage, attTotalPages, setAttTotalPages, attTotal, setAttTotal, attTypeFilter, setAttTypeFilter, exportLoading, setExportLoading, refreshingCache, setRefreshingCache, refreshingAll, setRefreshingAll, lastRefreshJob, setLastRefreshJob, refreshHistory, setRefreshHistory, showRefreshHistory, setShowRefreshHistory, pdfPreview, setPdfPreview, sendEmail, setSendEmail, rectifyModal, setRectifyModal, nomSign, setNomSign, gedLoading, setGedLoading, isAdmin, isSuperAdmin, fetchStats, fetchList, fetchAttestations, handleExportCSV, handleExportExcel, refreshAll, handleViewDomiumDoc, closePdfPreview, downloadFromPreview, fetchLastRefreshAllJob, fetchRefreshHistory, handleRefreshCacheAll, handleRefreshCache, handleDownloadDomiumDoc, openSendEmailDialog, submitSendEmail, openNomSignDialog, submitNomSign, handleClasserGED, getAuthHeader, userRole };
}
